import type { BalanceTransactionEntry } from '@nadoutong/contracts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PlatformApiError } from '../api/client.ts';
import { useBalance, useRedeemGiftCard, useTransactions } from '../api/useBilling.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatTimestamp, formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_MONO } from '../styles/forms.ts';

/**
 * Billing page — balance, transaction ledger, and redeem gift card.
 *
 * Topup (Epic #84 W4): a "Top up balance" CTA links to `/billing/topup`
 * which drives the ZPay (易支付) flow. This page stays USD-only — CNY
 * only surfaces on the topup form + receipt. See `BillingTopupPage.tsx`.
 *
 * Ledger paging uses simple limit/offset (20 per page). React Query
 * caches each page independently because the query key includes the
 * offset — no optimistic stitching required.
 */

const PAGE_SIZE = 20;

export function Billing(): JSX.Element {
  const t = useT();
  const balanceQ = useBalance();

  return (
    <section data-testid="billing-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('billing.title')}
        </h2>
        <p className="text-sm text-outline">{t('billing.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <article
          data-testid="billing-balance-card"
          className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
        >
          <header className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
              {t('billing.balance.title')}
            </h3>
            {balanceQ.data !== undefined ? (
              <StatusChip
                variant={balanceQ.data.balanceUsdCents > 0 ? 'ok' : 'neutral'}
                label={
                  balanceQ.data.balanceUsdCents > 0
                    ? t('billing.balance.status.funded')
                    : t('billing.balance.status.zero')
                }
              />
            ) : null}
          </header>
          {balanceQ.isLoading ? (
            <div className="flex flex-col gap-3" data-testid="billing-balance-loading">
              <Skeleton className="h-12 w-40" />
              <Skeleton className="h-3 w-48" />
              <span className="sr-only">{t('billing.balance.loading')}</span>
            </div>
          ) : balanceQ.isError ? (
            <p className="text-sm text-error" role="alert">
              {t('billing.balance.error')}
            </p>
          ) : balanceQ.data !== undefined ? (
            <>
              <span
                data-testid="billing-balance-value"
                className="text-5xl font-semibold tracking-tight text-primary"
              >
                {formatUsdCents(balanceQ.data.balanceUsdCents)}
              </span>
              <span className="text-xs text-outline">
                {t('billing.balance.updated', {
                  timestamp: formatTimestamp(balanceQ.data.updatedAt),
                })}
              </span>
            </>
          ) : null}
          <Link
            to="/billing/topup"
            data-testid="billing-topup-entry"
            className="mt-2 inline-flex items-center justify-between gap-2 rounded-lg bg-primary-container px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            <span className="inline-flex items-center gap-2">
              <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
                add
              </span>
              <span>{t('billing.topup.entry')}</span>
            </span>
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              arrow_forward
            </span>
          </Link>
        </article>

        <RedeemCard />
      </div>

      <TransactionsCard />
    </section>
  );
}

function RedeemCard(): JSX.Element {
  const t = useT();
  const redeemM = useRedeemGiftCard();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      setError(t('billing.redeem.error.code_required'));
      return;
    }
    try {
      const result = await redeemM.mutateAsync({ code: trimmed });
      setCode('');
      const message = t('billing.redeem.toast.success', {
        amount: formatUsdCents(result.amountUsdCents),
        balance: formatUsdCents(result.newBalanceUsdCents),
      });
      setSuccessMessage(message);
      toast.success(message);
    } catch (err) {
      const message =
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <article
      data-testid="billing-redeem-card"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('billing.redeem.title')}
        </h3>
      </header>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('billing.redeem.code_label')}
          </span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            disabled={redeemM.isPending}
            placeholder={t('billing.redeem.code_placeholder')}
            data-testid="billing-redeem-code"
            className={INPUT_CLASS_MONO}
            autoComplete="off"
          />
        </label>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="billing-redeem-error"
          >
            {error}
          </p>
        ) : null}
        {successMessage !== null ? (
          <p
            className="rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-medium text-primary"
            role="status"
            data-testid="billing-redeem-toast"
          >
            {successMessage}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={redeemM.isPending}
            data-testid="billing-redeem-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-primary-glow disabled:opacity-40 disabled:pointer-events-none"
          >
            {redeemM.isPending ? t('billing.redeem.submitting') : t('billing.redeem.submit')}
          </button>
        </div>
      </form>
    </article>
  );
}

function TransactionsCard(): JSX.Element {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const txQ = useTransactions({ limit: PAGE_SIZE, offset });
  const balanceQ = useBalance();
  const rows = txQ.data?.transactions ?? [];
  const hasMore = rows.length === PAGE_SIZE;

  // Running balance is only computable on the first page (offset === 0)
  // because we walk backwards from the current balance through each
  // most-recent transaction. On deeper pages we'd need every
  // in-between amount which the server doesn't return. Cells render
  // "—" when balance is unknown.
  const runningBalances = computeRunningBalances(
    rows,
    offset === 0 ? (balanceQ.data?.balanceUsdCents ?? null) : null,
  );

  return (
    <article
      data-testid="billing-transactions-card"
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('billing.transactions.title')}
        </h3>
        <span
          className="text-[10px] uppercase tracking-[0.1em] text-outline"
          data-testid="billing-transactions-offset"
        >
          {offset === 0
            ? t('billing.transactions.latest')
            : t('billing.transactions.page', { page: String(Math.floor(offset / PAGE_SIZE) + 1) })}
        </span>
      </header>

      {txQ.isLoading ? (
        <div className="flex flex-col gap-2" data-testid="billing-transactions-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10" />
          ))}
          <span className="sr-only">{t('billing.transactions.loading')}</span>
        </div>
      ) : txQ.isError ? (
        <p className="text-sm text-error" role="alert">
          {t('billing.transactions.error')}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-outline" data-testid="billing-transactions-empty">
          {t('billing.transactions.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-sm"
            data-testid="billing-transactions-table"
          >
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
                <th className="px-3 py-2 font-semibold">{t('billing.transactions.column.date')}</th>
                <th className="px-3 py-2 font-semibold">{t('billing.transactions.column.type')}</th>
                <th className="px-3 py-2 font-semibold">
                  {t('billing.transactions.column.amount_usd')}
                </th>
                <th className="px-3 py-2 font-semibold">
                  {t('billing.transactions.column.running')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx, idx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  runningBalanceCents={runningBalances[idx] ?? null}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0 || txQ.isFetching}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          data-testid="billing-transactions-prev"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
        >
          {t('billing.transactions.newer')}
        </button>
        <button
          type="button"
          disabled={!hasMore || txQ.isFetching}
          onClick={() => setOffset(offset + PAGE_SIZE)}
          data-testid="billing-transactions-next"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
        >
          {t('billing.transactions.older')}
        </button>
      </div>
    </article>
  );
}

interface TransactionRowProps {
  readonly tx: BalanceTransactionEntry;
  readonly runningBalanceCents: number | null;
}

function TransactionRow({ tx, runningBalanceCents }: TransactionRowProps): JSX.Element {
  const t = useT();
  const isCredit = tx.amountUsdCents > 0;
  return (
    <tr data-testid={`billing-tx-row-${tx.id}`} className="odd:bg-surface-container-low">
      <td className="px-3 py-2 font-mono text-on-surface-variant">
        {formatTimestamp(tx.createdAt)}
      </td>
      <td className="px-3 py-2 text-on-surface">
        <StatusChip
          variant={isCredit ? 'ok' : 'neutral'}
          label={transactionTypeLabel(t, tx.type)}
          testId={`billing-tx-type-${tx.id}`}
        />
      </td>
      <td
        className={`px-3 py-2 font-mono font-semibold ${isCredit ? 'text-primary' : 'text-error'}`}
        data-testid={`billing-tx-amount-${tx.id}`}
      >
        {formatUsdCents(tx.amountUsdCents, true)}
      </td>
      <td
        className="px-3 py-2 font-mono text-on-surface-variant"
        data-testid={`billing-tx-running-${tx.id}`}
      >
        {runningBalanceCents !== null ? formatUsdCents(runningBalanceCents) : '—'}
      </td>
    </tr>
  );
}

function transactionTypeLabel(t: ReturnType<typeof useT>, txType: string): string {
  switch (txType) {
    case 'charge':
      return t('billing.transactions.type.charge');
    case 'gift_card_redeem':
      return t('billing.transactions.type.gift_card_redeem');
    case 'ops_adjust':
      return t('billing.transactions.type.ops_adjust');
    case 'topup':
      return t('billing.transactions.type.topup');
    default:
      return txType.replace(/_/g, ' ');
  }
}

/**
 * Walk the DESC-ordered transaction list and compute the running
 * balance AFTER each row. Row 0 (most-recent) settles into the passed
 * `currentBalanceCents`; row N's running balance = row N-1's running
 * balance minus row N-1's amount.
 *
 * When `currentBalanceCents` is null (because the caller is on a
 * non-first page or the balance query hasn't loaded yet) the function
 * returns an all-null array — the UI falls back to rendering "—".
 */
function computeRunningBalances(
  rows: readonly BalanceTransactionEntry[],
  currentBalanceCents: number | null,
): readonly (number | null)[] {
  if (currentBalanceCents === null) {
    return rows.map(() => null);
  }
  const out: number[] = [];
  let runner = currentBalanceCents;
  for (const tx of rows) {
    out.push(runner);
    runner -= tx.amountUsdCents;
  }
  return out;
}
